import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/react-hooks';
import { Col, Row, Button } from 'reactstrap';
import { GET_TEMPLATES } from '../../query/query';
import { UPDATE_TEMPLATE } from '../../mutation/mutation';
import Template from './template-components/template';
import Filters from './filters';
import { allFontsAreUploadedInApp, getTemplateFonts, someFontIsDeprecated } from './utils';
import SearchTemplateId from './search-bar/search-templates-id';
import SearchTemplateByName from './search-bar/search-template-by-name';
import SearchTemplateByCategory from './search-bar/search-templates-by-category';
import SearchTemplateByReleaseDate from './search-bar/search-templates-releaseDate';

const DEFAULT_FILTERS = {
  status: ['ACCEPTED'],
  fontsUploaded: ['yes', 'no', 'deprecated'],
  isGif: ['yes', 'no'],
  isPremium: ['yes', 'no'],
  internalNote: ['yes', 'no'],
  sortBy: ['rank'],
};

const DEFAULT_SEARCH_OPTIONS = {
  ids: '',
  name: '',
  category: {},
  isGif: false,
  isPremium: true,
};

const sortByRank = ({ rank: rankA }, { rank: rankB }) => {
  if (rankA === null || rankA === undefined) return -1;
  if (rankB === null || rankB === undefined) return 1;
  return (rankA || 0) - (rankB || 0);
};

const sortByName = ({ name: nameA }, { name: nameB }) => {
  if (nameA === null || nameA === undefined) return -1;
  if (nameB === null || nameB === undefined) return 1;
  return (nameA || '').localeCompare(nameB || 0);
};

const sortByDate = ({ _id: idA }, { _id: idB }) => {
  if (idA === null || idA === undefined) return -1;
  if (idB === null || idB === undefined) return 1;
  const dateA = new Date(parseInt(idA.toString().slice(0, 8), 16) * 1000);
  const dateB = new Date(parseInt(idB.toString().slice(0, 8), 16) * 1000);
  return ((dateA.getTime() > dateB.getTime()) ? -1 : 1);
};

const TemplatesManagerPage = ({
  savGetTemplates,
  savGetTemplatesLoading,
  templateCategories,
}) => {
  const [templates, setTemplates] = useState([]);
  const [subCategoriesList, setSubCategoriesList] = useState([]);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [searchOptions, setSearchOptions] = useState(DEFAULT_SEARCH_OPTIONS);
  const [searchOptionsEdited, setSearchOptionsEdited] = useState(false);

  // Use this state to avoid template disappearing after update because of filters
  const [updatedTemplateIds, setUpdatedTemplateIds] = useState([]);

  const [updateTemplate] = useMutation(UPDATE_TEMPLATE, {
    update: (cache, { data: { savUpdateTemplate } }) => {
      const { savGetTemplates: savGetTemplatesTmp } = cache.readQuery({ query: GET_TEMPLATES });
      const savGetTemplatesTmpCopy = [...savGetTemplatesTmp];
      const index = savGetTemplatesTmpCopy.map(({ _id }) => _id).indexOf(savUpdateTemplate._id);
      const buffTemplateEdited = { ...savGetTemplatesTmpCopy[index] };
      Object.entries(savUpdateTemplate).forEach(([key, value]) => {
        if (key) buffTemplateEdited[key] = value;
      });
      savGetTemplatesTmpCopy[index] = buffTemplateEdited;
      cache.writeQuery({
        query: GET_TEMPLATES,
        data: { savGetTemplates: savGetTemplatesTmpCopy },
      });
    },
  });

  const handleUpdateTemplate = async ({ variables }) => {
    const { templateId } = variables;
    setUpdatedTemplateIds([...updatedTemplateIds, templateId]);
    await updateTemplate({ variables });
  };

  const findSubCategoriesOfTemplates = (templatesArray) => templatesArray.map((template) => ({
    ...template,
    subCategories: template.subCategories.map((subCategoryId) => {
      if (!subCategoryId) return { titleEn: 'Empty tag' };
      return subCategoriesList.find(({ _id }) => _id === subCategoryId) || { titleEn: `Unknown tag (${subCategoryId})` };
    }),
  }));

  const updateSearchOption = (optionName, value) => {
    setSearchOptions({ ...searchOptions, [optionName]: value });
    setSearchOptionsEdited(true);
  };

  const searchTemplatesByFilters = () => {
    const {
      ids: idsToSearch,
      name: nameToSearch,
      category: { _id: categoryId } = {},
      releaseDateToSearch,
    } = searchOptions;
    const idsArrayToSearch = idsToSearch ? idsToSearch.split(' ') : [];
    const foundTemplates = savGetTemplates.filter(({
      status = 'PENDING',
      elements = [],
      isGif,
      premium,
      name,
      subCategories,
      releaseDate,
      internalNote,
      _id,
    }) => (
      (filters.status.includes(status)
        && ((filters.fontsUploaded.includes('yes') && allFontsAreUploadedInApp(getTemplateFonts(elements)))
          || (filters.fontsUploaded.includes('no') && !allFontsAreUploadedInApp(getTemplateFonts(elements)))
          || (filters.fontsUploaded.includes('deprecated') && someFontIsDeprecated(getTemplateFonts(elements))))
        && ((filters.isPremium.includes('yes') && premium) || (filters.isPremium.includes('no') && !premium))
        && ((filters.isGif.includes('yes') && isGif) || (filters.isGif.includes('no') && !isGif))
        && ((filters.internalNote.includes('yes') && internalNote)
          || (filters.internalNote.includes('no') && !internalNote))
        && (idsToSearch ? idsArrayToSearch.find((id) => id === _id) !== undefined : true)
        && (nameToSearch ? name.toLowerCase().match(nameToSearch) : true)
        && ((categoryId && categoryId !== 'NONE') ? subCategories.includes(categoryId) : true)
        && ((categoryId && categoryId === 'NONE') ? subCategories.length === 0 : true)
        && (releaseDateToSearch ? releaseDateToSearch === releaseDate : true)
      )
      || updatedTemplateIds.includes(_id)
    ));
    if (filters.sortBy.includes('name')) {
      setTemplates(findSubCategoriesOfTemplates(foundTemplates).sort(sortByName));
    } else if (filters.sortBy.includes('date')) {
      setTemplates(findSubCategoriesOfTemplates(foundTemplates).sort(sortByDate));
    } else {
      setTemplates(findSubCategoriesOfTemplates(foundTemplates).sort(sortByRank));
    }
  };

  useEffect(() => {
    setSearchOptionsEdited(false);
    if (!savGetTemplates.length || !subCategoriesList.length) return;
    searchTemplatesByFilters();
  }, [savGetTemplates, templateCategories, filters, searchOptionsEdited, updatedTemplateIds]);

  useEffect(() => {
    setLimit(100);
    setUpdatedTemplateIds([]);
  }, [filters, searchOptionsEdited]);

  useEffect(() => {
    if (!templateCategories.length) return;
    setSubCategoriesList(
      [].concat.apply([], [...templateCategories.map(({ subCategories }) => subCategories)]),
    );
  }, [templateCategories]);

  const [limit, setLimit] = useState(100);

  const loadMore = () => {
    setLimit(limit + 100);
  };

  const loadAll = () => {
    setLimit(Infinity);
  };

  if (savGetTemplatesLoading) {
    return <div style={styles.loading}>Loading templates from server... Please wait. </div>;
  }

  return (
    [
      <Row key="searchBars" style={styles.row}>
        <Col style={styles.col}>
          <SearchTemplateId key="templates-id-filter" idToSearch={searchOptions.ids} setIdToSearch={(value) => updateSearchOption('ids', value)} />
        </Col>
        <Col style={styles.col}>
          <SearchTemplateByName key="templates-name-filter" setName={(value) => updateSearchOption('name', value)} />
        </Col>
      </Row>,
      <Row key="searchBars2" style={styles.row}>
        <Col style={styles.col}>
          <SearchTemplateByCategory
            key="templates-category-filter"
            categories={[{ _id: 'NONE', titleEn: 'NONE' }, ...subCategoriesList]}
            category={searchOptions.category}
            setCategory={(value) => updateSearchOption('category', value)}
            style={styles.categoriesFilterContainer}
          />
        </Col>
        <Col style={styles.col}>
          <SearchTemplateByReleaseDate
            key="templates-release-date-filter"
            releaseDate={searchOptions.releaseDate}
            setReleaseDate={(value) => updateSearchOption('releaseDate', value)}
          />
        </Col>
      </Row>,
      <Filters key="templates-filter" filters={filters} setFilters={setFilters} />,
      <div key="templates-list" style={styles.listContainer}>
        {
          templates.slice(0, Math.min(templates.length, limit)).map((template, index) => (
            <Template
              index={index}
              // eslint-disable-next-line react/no-array-index-key
              key={template._id}
              subCategoriesList={subCategoriesList}
              template={template}
              updateTemplate={handleUpdateTemplate}
            />
          ))
        }
      </div>,
      templates.length > Math.min(templates.length, limit) && (
        <div>
          <div style={styles.loadMore}>
            <Button
              onClick={loadMore}
              color="success"
            >
              {`Load more (${templates.length - limit} remaining)`}
            </Button>
          </div>
          <div style={styles.loadMore}>
            <Button
              onClick={loadAll}
              color="success"
            >
              Load all
            </Button>
          </div>
        </div>
      ),
    ]
  );
};

const styles = {
  listContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
  },
  loadMore: {
    marginTop: 20,
    textAlign: 'center',
  },
  col: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  categoriesFilterContainer: {
    width: 400,
  },
  loading: {
    fontSize: 18,
    width: '100%',
    textAlign: 'center',
    marginTop: 30,
  },
};

export default TemplatesManagerPage;



// WEBPACK FOOTER //
// ./src/components/templates-manager/templates-validation.js